import React, { useMemo, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import { Box, Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import dayjs, { Dayjs } from 'dayjs';

import dateFormatter from '~/utils/dateFormatter';

type SchedulePeriod = {
  from?: string | null;
  to?: string | null;
};

type PromotionSchedule = {
  activePeriod?: SchedulePeriod;
  travelPeriod?: SchedulePeriod;
  active_period?: SchedulePeriod;
  travel_period?: SchedulePeriod;
};

type PromotionDiscount = {
  id?: string;
  created_at?: string;
  discount_name?: string | null;
  discount_percent?: number | null;
  luxplus_discount_percent?: number | null;
  min_los?: number | null;
  max_los?: number | null;
  min_days_to_arrival?: number | null;
  max_days_to_arrival?: number | null;
  schedules?: Array<PromotionSchedule>;
};

type RatePlanWithPromotions = {
  id: string;
  name: string;
  rate_plan_code?: string;
  discounts?: Array<PromotionDiscount> | Record<string, PromotionDiscount>;
};

type Props = {
  ratePlans: Array<RatePlanWithPromotions>;
  vendorId: string;
  roomsByRatePlan?: Record<string, Array<{ name: string; link: string }>>;
  showRoomsColumn?: boolean;
};

type PromotionFilterValue = 'all' | 'current' | 'future' | 'expired';

type PromotionRow = {
  id: string;
  ratePlanId: string;
  ratePlanName: string;
  discounts: Array<PromotionDiscount>;
};

const PROMOTION_FILTER_OPTIONS: Array<{ value: PromotionFilterValue; label: string }> = [
  { value: 'all', label: 'Show all rates' },
  { value: 'current', label: 'Show rates with current promos' },
  { value: 'future', label: 'Show rates with future promos' },
  { value: 'expired', label: 'Show rates with expired promos' },
];

function getScheduleDates(schedule?: PromotionSchedule): {
  activeFrom: Dayjs | null;
  activeTo: Dayjs | null;
  travelFrom: Dayjs | null;
  travelTo: Dayjs | null;
} {
  const activeFrom = schedule?.activePeriod?.from ?? schedule?.active_period?.from;
  const activeTo = schedule?.activePeriod?.to ?? schedule?.active_period?.to;
  const travelFrom = schedule?.travelPeriod?.from ?? schedule?.travel_period?.from;
  const travelTo = schedule?.travelPeriod?.to ?? schedule?.travel_period?.to;

  return {
    activeFrom: activeFrom ? dayjs(activeFrom) : null,
    activeTo: activeTo ? dayjs(activeTo) : null,
    travelFrom: travelFrom ? dayjs(travelFrom) : null,
    travelTo: travelTo ? dayjs(travelTo) : null,
  };
}

function matchesPromotionFilter(discount: PromotionDiscount, filter: PromotionFilterValue, now: Dayjs): boolean {
  if (filter === 'all') {
    return true;
  }

  const schedule = discount.schedules?.[0];
  const { activeFrom, activeTo } = getScheduleDates(schedule);

  if (!activeFrom || !activeTo || !activeFrom.isValid() || !activeTo.isValid()) {
    return false;
  }

  if (filter === 'current') {
    return (now.isAfter(activeFrom) || now.isSame(activeFrom, 'day')) && (now.isBefore(activeTo) || now.isSame(activeTo, 'day'));
  }

  if (filter === 'future') {
    return now.isBefore(activeFrom, 'day');
  }

  return now.isAfter(activeTo, 'day');
}

function formatOptionalNumber(value?: number | null) {
  return value ?? '—';
}

function renderMultiValue(discounts: Array<PromotionDiscount>, formatter: (discount: PromotionDiscount) => React.ReactNode) {
  return (
    <Stack py={1} spacing={1}>
      {discounts.map((discount, index) => (
        <Box key={`${discount.id ?? index}-${index}`}>
          <Typography variant="body2">{formatter(discount)}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

export default function VendorRatePlansPromotionsList({ ratePlans, vendorId, roomsByRatePlan, showRoomsColumn = true }: Props) {
  const [promotionFilter, setPromotionFilter] = useState<PromotionFilterValue>('all');

  const handlePromotionFilterChange = (event: SelectChangeEvent) => {
    setPromotionFilter(event.target.value as PromotionFilterValue);
  };

  const rows = useMemo<Array<PromotionRow>>(() => {
    const now = dayjs();

    return ratePlans.reduce<Array<PromotionRow>>((acc, ratePlan) => {
      const allDiscounts = Array.isArray(ratePlan.discounts) ? ratePlan.discounts : Object.values(ratePlan.discounts ?? {});
      const discountsWithSchedules = allDiscounts.filter((discount) => (discount.schedules?.length ?? 0) > 0);
      const filteredDiscounts = discountsWithSchedules.filter((discount) => matchesPromotionFilter(discount, promotionFilter, now));

      if (filteredDiscounts.length === 0) {
        return acc;
      }

      acc.push({
        id: ratePlan.id,
        ratePlanId: ratePlan.id,
        ratePlanName: `${ratePlan.name}${ratePlan.rate_plan_code ? ` (${ratePlan.rate_plan_code})` : ''}`,
        discounts: filteredDiscounts,
      });

      return acc;
    }, []);
  }, [promotionFilter, ratePlans]);

  const columns: Array<GridColDef> = [
    {
      field: 'ratePlanName',
      headerName: 'Rate Plan',
      minWidth: 220,
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: 'discount_name',
      headerName: 'Name',
      minWidth: 160,
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => discount.discount_name || 'Unnamed');
      },
      display: 'flex',
    },
    {
      field: 'active_from',
      headerName: 'Active From',
      minWidth: 150,
      width: 170,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => {
          const { activeFrom } = getScheduleDates(discount.schedules?.[0]);
          return dateFormatter(activeFrom?.toISOString());
        });
      },
      display: 'flex',
    },
    {
      field: 'active_to',
      headerName: 'Active To',
      minWidth: 150,
      width: 170,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => {
          const { activeTo } = getScheduleDates(discount.schedules?.[0]);
          return dateFormatter(activeTo?.toISOString());
        });
      },
      display: 'flex',
    },
    {
      field: 'travel_from',
      headerName: 'Travel From',
      minWidth: 150,
      width: 170,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => {
          const { travelFrom } = getScheduleDates(discount.schedules?.[0]);
          return dateFormatter(travelFrom?.toISOString());
        });
      },
      display: 'flex',
    },
    {
      field: 'travel_to',
      headerName: 'Travel To',
      minWidth: 150,
      width: 170,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => {
          const { travelTo } = getScheduleDates(discount.schedules?.[0]);
          return dateFormatter(travelTo?.toISOString());
        });
      },
      display: 'flex',
    },
    {
      field: 'discount_percent',
      headerName: '%',
      minWidth: 60,
      width: 70,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => formatOptionalNumber(discount.discount_percent));
      },
      display: 'flex',
    },
    {
      field: 'luxplus_discount_percent',
      headerName: 'Lux Plus %',
      minWidth: 75,
      width: 85,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => formatOptionalNumber(discount.luxplus_discount_percent));
      },
      display: 'flex',
    },
    {
      field: 'min_los',
      headerName: 'Min LOS',
      minWidth: 70,
      width: 85,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => formatOptionalNumber(discount.min_los));
      },
      display: 'flex',
    },
    {
      field: 'max_los',
      headerName: 'Max LOS',
      minWidth: 70,
      width: 85,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => formatOptionalNumber(discount.max_los));
      },
      display: 'flex',
    },
    {
      field: 'min_days_to_arrival',
      headerName: 'Min Days to Arrival',
      minWidth: 90,
      width: 110,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderHeader: () => (
        <Box component="span" sx={{ lineHeight: 1.2 }}>
          Min Days to
          <br />
          Arrival
        </Box>
      ),
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => formatOptionalNumber(discount.min_days_to_arrival));
      },
      display: 'flex',
    },
    {
      field: 'max_days_to_arrival',
      headerName: 'Max Days to Arrival',
      minWidth: 90,
      width: 110,
      flex: 0,
      resizable: true,
      disableColumnMenu: true,
      sortable: false,
      renderHeader: () => (
        <Box component="span" sx={{ lineHeight: 1.2 }}>
          Max Days to
          <br />
          Arrival
        </Box>
      ),
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => formatOptionalNumber(discount.max_days_to_arrival));
      },
      display: 'flex',
    },
    {
      field: 'created_at',
      headerName: 'Created at',
      minWidth: 140,
      flex: 0.9,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return renderMultiValue(params.row.discounts, (discount) => dateFormatter(discount.created_at));
      },
      display: 'flex',
    },
    {
      field: 'edit',
      headerName: 'Edit promos',
      minWidth: 90,
      flex: 0,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PromotionRow>) => {
        return (
          <Button
            component={Link}
            to={`/vendors/${vendorId}/edit-rate-plan/${params.row.ratePlanId}#promotional-discounts`}
            variant="text"
          >
            Edit
          </Button>
        );
      },
      display: 'flex',
    },
  ];

  const roomsColumn: GridColDef<PromotionRow> = {
    field: 'rooms',
    headerName: 'Rooms',
    disableColumnMenu: true,
    minWidth: 220,
    flex: 1.5,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PromotionRow>) => {
      const rooms = roomsByRatePlan?.[params.row.ratePlanId] ?? [];
      if (!rooms.length) {
        return '—';
      }

      return (
        <Box sx={{ py: 1 }}>
          {rooms.map((room, index) => (
            <React.Fragment key={room.link}>
              <Link to={room.link}>{room.name}</Link>
              {index < rooms.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}
        </Box>
      );
    },
    display: 'flex',
  };

  const allColumns: Array<GridColDef> = showRoomsColumn ? [columns[0], roomsColumn, ...columns.slice(1)] : columns;

  return (
    <>
      <Stack mb={2} direction="row" spacing={2}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel id="promotions-filter-label">Promotions filter</InputLabel>
          <Select
            labelId="promotions-filter-label"
            id="promotions-filter"
            value={promotionFilter}
            label="Promotions filter"
            onChange={handlePromotionFilterChange}
          >
            {PROMOTION_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <DataGrid
        className="T-rate-plan-promotions-table"
        rows={rows}
        columns={allColumns}
        getRowId={(row) => row.id}
        getRowHeight={() => 'auto'}
        autoHeight
        disableRowSelectionOnClick
        slots={{
          pagination: null,
        }}
        sx={{ mt: 2 }}
      />
    </>
  );
}
